import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SectionGlow from '@/components/common/SectionGlow';
import axios from 'axios';
import { Twitter, Instagram, Youtube, Music2, MessageCircle, Heart, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM = {
  twitter: { label: 'X', Icon: Twitter, color: '#1DA1F2', handle: '𝕏' },
  tiktok: { label: 'TikTok', Icon: Music2, color: '#EE1D52' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#E1306C' },
  youtube: { label: 'YouTube', Icon: Youtube, color: '#FF0000' },
  other: { label: 'Comment', Icon: MessageCircle, color: 'var(--accent-primary)' },
};

const ProofCard = ({ card }) => {
  const { t } = useTranslation();
  const p = PLATFORM[card.platform] || PLATFORM.other;
  const { Icon } = p;
  const label = card.platform === 'other' || !PLATFORM[card.platform] ? t('home.social.comment') : p.label;
  const handle = card.username.startsWith('@') ? card.username : `@${card.username}`;
  const inner = (
    <>
      <div className="flex items-center gap-2.5 mb-3">
        <span className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ background: `${p.color}1A` }}>
          <Icon size={16} style={{ color: p.color }} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{handle}</p>
          <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>{label}</p>
        </div>
        {card.link_url && <ExternalLink size={14} className="ml-auto flex-shrink-0" style={{ color: 'var(--text-secondary)' }} />}
      </div>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }} data-testid={`social-proof-comment-${card.id}`}>
        {card.comment_text}
      </p>
      {card.like_count && (
        <div className="flex items-center gap-1.5 mt-4">
          <Heart size={14} style={{ color: p.color }} fill={p.color} />
          <span className="text-xs font-medium tabular-nums" style={{ color: 'var(--text-secondary)' }}>{card.like_count}</span>
        </div>
      )}
    </>
  );

  const style = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-color)',
    borderTop: `3px solid ${p.color}`,
  };
  const cls = 'block rounded-2xl p-5 snap-start min-w-[82%] md:min-w-0 transition-shadow hover:shadow-[0_8px_28px_rgba(0,0,0,0.08)]';

  if (card.link_url) {
    return (
      <a href={card.link_url} target="_blank" rel="noopener noreferrer" data-testid={`social-proof-card-${card.id}`} className={cls} style={style}>
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={`social-proof-card-${card.id}`} className={cls} style={style}>
      {inner}
    </div>
  );
};

// Section 2 (01-home.md): curated social media comment cards. Visible to everyone.
// Horizontal scroll carousel on mobile, static 3-col grid on desktop. Hidden when empty.
const SocialProof = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const [data, setData] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/home/social-proof`, { params: { lang } }).then((r) => setData(r.data || { heading: '', cards: [] })).catch(() => setData({ heading: '', cards: [] }));
  }, [lang]);

  if (data === undefined) return null;
  const cards = data.cards || [];
  if (!cards.length) return null;

  return (
    <section data-testid="social-proof-section" className="relative overflow-hidden py-20 md:py-28" style={{ background: 'var(--bg-primary)' }}>
      <SectionGlow position="50% 0%" strength={9} />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-h2 font-bold text-center"
          style={{ color: 'var(--text-primary)' }}
          data-testid="social-proof-heading"
        >
          {data.heading || 'What people are saying'}
        </motion.h2>

        <div
          data-testid="social-proof-list"
          className="mt-10 flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-2 md:pb-0"
          style={{ scrollbarWidth: 'none' }}
        >
          {cards.map((c) => <ProofCard key={c.id} card={c} />)}
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
